import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  makeStyles,
  shorthands,
  tokens,
  Button,
  ToolbarButton,
  Text,
  Badge,
  Menu,
  MenuTrigger,
  MenuPopover,
  MenuList,
  MenuItem,
} from '@fluentui/react-components';
import { PanelLeft24Regular, PanelLeftContract24Regular, MoreHorizontal24Regular, Navigation24Regular, Dismiss20Regular, PanelRight24Regular } from '@fluentui/react-icons';
import { getToolbarForRoute, getLayoutConfigForRoute } from '../config/toolbars';
import { useLayout } from '../contexts/LayoutContext';
import { useSelection } from '../contexts/SelectionContext';
import { useIsMobile } from '../hooks/useIsMobile';

interface HeaderProps {
  contentSidebarOpen?: boolean;
  onContentSidebarToggle?: () => void;
  showContentSidebarToggle?: boolean;
  customActions?: React.ReactNode;
  /** Page-supplied title slot rendered to the right of the sidebar
   *  toggle. When set, it replaces the default `pageContext` text and
   *  lets the caller compose its own structure (breadcrumbs, avatar +
   *  name, etc.). Used by the customer profile to inline the global
   *  Header into its workspace card. */
  customTitle?: React.ReactNode;
  /** Page-supplied buttons rendered immediately after the global
   *  sidebar toggle (e.g. a per-page filter-panel toggle). Sits in the
   *  left section so chrome controls cluster together. */
  leftActions?: React.ReactNode;
  /** When true, strip the Header's own background / shadow / rounded
   *  corners. Used on pages that render Header inline as the first row
   *  of their workspace card — the card already provides the surface
   *  chrome, so the Header should sit transparent on top of it. Default
   *  false (legacy floating mode for not-yet-migrated pages). */
  inline?: boolean;
  /** On mobile, give the title slot (`customTitle`/`pageContext`) the top
   *  line on its own and drop the action cluster onto a second full-width
   *  row beneath it, so a rich title (breadcrumb + name + status pills)
   *  doesn't collide with the action buttons. Opt-in; desktop is unaffected. */
  stackTitleOnMobile?: boolean;
  /** Suppress the built-in "N selected" indicator. Pages that surface
   *  selection + bulk actions in their own table row (via BulkActionBar)
   *  set this so the count doesn't also pop into the header — which would
   *  shift the header layout the moment a row is selected. */
  hideSelectionInfo?: boolean;
}

const useStyles = makeStyles({
  header: {
    display: 'flex',
    alignItems: 'center',
    height: '40px',
    backgroundColor: tokens.colorNeutralBackground1,
    boxShadow: tokens.shadow8,
    width: '100%',
    ...shorthands.padding('0', '16px'),
  },
  // Inline mode: Header sits inside a workspace card that already owns
  // the background/shadow — strip Header's own chrome so the surface
  // reads as one continuous card. Switch to auto height + vertical
  // padding so the action buttons get breathing room above and below
  // instead of sitting flush against the card edge.
  headerInline: {
    backgroundColor: 'transparent',
    boxShadow: 'none',
    height: 'auto',
    minHeight: '48px',
    ...shorthands.padding('8px', '16px'),
  },
  leftSection: {
    display: 'flex',
    alignItems: 'center',
    columnGap: '8px',
    flex: 1,
    // Page-supplied title slots (breadcrumb + status pills + save badge)
    // can overflow on a 375px viewport. Allow them to wrap to a second
    // row and shrink so the chrome controls stay visible.
    '@media (max-width: 768px)': {
      flexWrap: 'wrap',
      rowGap: '4px',
      minWidth: 0,
    },
  },
  centerSection: {
    display: 'flex',
    alignItems: 'center',
    columnGap: '8px',
  },
  pageTitle: {
    fontSize: '20px',
    fontWeight: '600',
    marginRight: '16px',
  },
  titleWithBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginRight: '16px',
  },
  rightSection: {
    display: 'flex',
    alignItems: 'center',
    columnGap: '8px',
  },
  selectionInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    fontSize: '14px',
    color: tokens.colorNeutralForeground1,
  },
  pageContextContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginLeft: '8px',
  },
  // When the page supplies its own customTitle, let the slot stretch
  // across the leftSection so the title can right-align inner items
  // (e.g. /orders/:id parks the Payment pill against the toolbar).
  pageContextContainerCustom: {
    flexGrow: 1,
    minWidth: 0,
  },
  pageContextTitle: {
    fontSize: '14px',
    fontWeight: '500',
    color: tokens.colorNeutralForeground2,
  },
  pageContextBadge: {
    fontSize: '16px',
    fontWeight: '600',
    color: tokens.colorNeutralForeground1,
    backgroundColor: tokens.colorNeutralBackground3,
    ...shorthands.padding('4px', '12px'),
    ...shorthands.borderRadius('4px'),
  },
  // Column wrapper used when stacking the title onto a second mobile row.
  stackedWrapper: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
  },
  // For floating (non-inline) headers the wrapper carries the surface chrome
  // so both rows sit on one continuous header block. Inline headers inherit
  // their surface from the workspace card, so they stay transparent.
  stackedSurface: {
    backgroundColor: tokens.colorNeutralBackground1,
    boxShadow: tokens.shadow8,
  },
  // The second row that carries the action cluster beneath the title row.
  // Aligns to the header's horizontal padding and wraps long content. A top
  // border draws the divider between the two rows.
  secondRow: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    rowGap: '4px',
    minWidth: 0,
    ...shorthands.padding('8px', '16px'),
    ...shorthands.borderTop('1px', 'solid', tokens.colorNeutralStroke2),
  },
});

interface PageContext {
  title?: string;
  subtitle?: string;
  badge?: string;
}

const Header: React.FC<HeaderProps> = ({ contentSidebarOpen = false, onContentSidebarToggle, showContentSidebarToggle = true, customActions, customTitle, leftActions, inline = false, stackTitleOnMobile = false, hideSelectionInfo = false }) => {

  const styles = useStyles();
  const location = useLocation();
  const navigate = useNavigate();
  const [maintenanceTab, setMaintenanceTab] = useState('active');
  const [viewMode, setViewMode] = useState('table');
  const layoutConfig = getLayoutConfigForRoute(location.pathname);
  const [detailsPaneOpen, setDetailsPaneOpen] = useState(() => {
    // Initialize based on route config
    return layoutConfig.detailsPaneOpenByDefault || false;
  });
  const { sidebarOpen, toggleSidebar } = useLayout();
  const { selectedCount, clearSelection } = useSelection();
  const isMobile = useIsMobile();
  const [activePanel, setActivePanel] = useState<string | null>(null);
  const [pageContext, setPageContext] = useState<PageContext | null>(null);

  const toolbarConfig = getToolbarForRoute(location.pathname);
  const isMaintenancePage = location.pathname === '/maintenance';

  // Toggle details pane
  const toggleDetailsPane = () => {
    const newState = !detailsPaneOpen;
    setDetailsPaneOpen(newState);
    window.dispatchEvent(new CustomEvent('detailsPaneToggle', { detail: newState }));
  };


  // Listen for maintenance tab changes
  useEffect(() => {
    const handleTabChange = (event: CustomEvent) => {
      setMaintenanceTab(event.detail);
    };

    window.addEventListener('maintenanceTabChange', handleTabChange as EventListener);
    return () => {
      window.removeEventListener('maintenanceTabChange', handleTabChange as EventListener);
    };
  }, []);

  // Listen for navigation events
  useEffect(() => {
    const handleNavigateToNewOrder = () => {
      navigate('/orders/new');
    };

    window.addEventListener('navigateToNewOrder', handleNavigateToNewOrder);
    return () => {
      window.removeEventListener('navigateToNewOrder', handleNavigateToNewOrder);
    };
  }, [navigate]);

  // Listen for view mode changes
  useEffect(() => {
    const handleViewModeChange = (event: CustomEvent) => {
      setViewMode(event.detail);
    };

    window.addEventListener('viewModeChanged', handleViewModeChange as EventListener);
    return () => {
      window.removeEventListener('viewModeChanged', handleViewModeChange as EventListener);
    };
  }, []);


  // Listen for external details pane toggle events (from other components)
  useEffect(() => {
    const handleExternalToggle = (event: CustomEvent<boolean>) => {
      setDetailsPaneOpen(event.detail);
    };

    window.addEventListener('detailsPaneToggle', handleExternalToggle as EventListener);
    return () => {
      window.removeEventListener('detailsPaneToggle', handleExternalToggle as EventListener);
    };
  }, []);

  // Listen for details pane state changes from pages
  useEffect(() => {
    const handleStateChange = (event: CustomEvent<boolean>) => {
      setDetailsPaneOpen(event.detail);
    };

    window.addEventListener('detailsPaneStateChanged', handleStateChange as EventListener);
    return () => {
      window.removeEventListener('detailsPaneStateChanged', handleStateChange as EventListener);
    };
  }, []);

  // Update details pane state when navigating to a new page based on route config
  useEffect(() => {
    const newLayoutConfig = getLayoutConfigForRoute(location.pathname);
    const shouldBeOpen = newLayoutConfig.detailsPaneOpenByDefault || false;
    setDetailsPaneOpen(shouldBeOpen);
    // Dispatch event to sync with Layout
    window.dispatchEvent(new CustomEvent('detailsPaneToggle', { detail: shouldBeOpen }));
  }, [location.pathname]);

  // Listen for active panel changes (for button state)
  useEffect(() => {
    const handleActivePanelChange = (event: CustomEvent) => {
      setActivePanel(event.detail);
    };

    window.addEventListener('activePanelChange', handleActivePanelChange as EventListener);
    return () => {
      window.removeEventListener('activePanelChange', handleActivePanelChange as EventListener);
    };
  }, []);

  // Listen for page context changes (e.g., selected order number)
  useEffect(() => {
    const handlePageContextChange = (event: CustomEvent<PageContext | null>) => {
      setPageContext(event.detail);
    };

    window.addEventListener('setPageContext', handlePageContextChange as EventListener);
    return () => {
      window.removeEventListener('setPageContext', handlePageContextChange as EventListener);
    };
  }, []);

  // Clear page context when route changes
  useEffect(() => {
    setPageContext(null);
  }, [location.pathname]);

  // Filter toolbar buttons based on maintenance tab
  const filteredToolbarConfig = isMaintenancePage && toolbarConfig
    ? toolbarConfig.filter(item => {
      if (maintenanceTab === 'active') {
        // Hide SCHEDULE and PRINT on Active Reminders tab
        return item.label !== 'SCHEDULE' && item.label !== 'PRINT';
      }
      return true;
    })
    : toolbarConfig;

  // Separate primary and secondary buttons
  const primaryButtons = filteredToolbarConfig?.filter(item => item.appearance === 'primary') || [];
  const secondaryButtons = filteredToolbarConfig?.filter(item => item.appearance !== 'primary') || [];

  const renderButton = (item: any, index: number) => {
    // If the item has a custom component, render it
    if (item.customComponent) {
      const CustomComponent = item.customComponent;
      return <CustomComponent key={index} />;
    }
    // If the item has menuItems, render as a Menu
    if (item.menuItems && item.menuItems.length > 0) {
      return (
        <Menu key={index}>
          <MenuTrigger disableButtonEnhancement>
            <Button
              icon={item.icon}
              appearance={item.appearance || 'secondary'}
            >
              {item.label}
            </Button>
          </MenuTrigger>
          <MenuPopover>
            <MenuList>
              {item.menuItems.map((menuItem: any, menuIndex: number) => (
                <MenuItem key={menuIndex} icon={menuItem.icon} onClick={menuItem.onClick}>
                  {menuItem.label}
                </MenuItem>
              ))}
            </MenuList>
          </MenuPopover>
        </Menu>
      );
    }
    // Otherwise, render as a regular button
    // Use primary appearance if this is a view toggle button matching current view mode
    const isActiveView = item.viewToggle && item.viewToggle === viewMode;
    // Use primary appearance if this is a panel button and its panel is open
    const isPanelActive = item.layerKey && activePanel === item.layerKey;
    // On mobile, collapse labeled buttons to icon-only to save space (the
    // label moves to aria-label). Only when there's an icon to stand in for it.
    const iconOnly = isMobile && !!item.icon;
    return (
      <Button
        key={index}
        icon={item.icon}
        appearance={(isActiveView || isPanelActive) ? 'primary' : (item.appearance || 'secondary')}
        onClick={item.onClick}
        aria-label={iconOnly ? item.label : undefined}
      >
        {iconOnly ? undefined : item.label}
      </Button>
    );
  };

  // The title slot (page-supplied customTitle, or legacy event-driven
  // pageContext). Extracted so it can render inline in the first row or, on
  // mobile with stackTitleOnMobile, on its own second row below.
  const titleSlot = customTitle ? (
    <div className={`${styles.pageContextContainer} ${styles.pageContextContainerCustom}`}>{customTitle}</div>
  ) : pageContext ? (
    <div className={styles.pageContextContainer}>
      {pageContext.title && (
        <span className={styles.pageContextTitle}>{pageContext.title}</span>
      )}
      {pageContext.badge && (
        <span className={styles.pageContextBadge}>{pageContext.badge}</span>
      )}
      {pageContext.subtitle && (
        <span className={styles.pageContextTitle}>{pageContext.subtitle}</span>
      )}
    </div>
  ) : null;

  // Only stack when there's both a title to give the top row AND an action
  // cluster to drop below it — otherwise stacking would leave an empty
  // second row (just a divider strip).
  const showSelectionInfo = !hideSelectionInfo && selectedCount > 0;
  const hasActions =
    (!layoutConfig.embedToolbarInPage &&
      (showSelectionInfo || secondaryButtons.length > 0 || primaryButtons.length > 0)) ||
    !!customActions;
  const stackTitle = stackTitleOnMobile && isMobile && !!titleSlot && hasActions;

  // Right-hand action cluster (selection info, page actions, toolbar
  // buttons). Extracted so it can sit in the first row (default) or drop onto
  // the second mobile row beneath the title when stacking.
  const actionsContent = (
    <>
      {/* When `embedToolbarInPage` is true, the page renders these via
          <PageActionBar /> in its workspace surface; skip them here so we
          don't double-render. Custom actions stay in the global header
          since they're caller-controlled (rare; not page toolbar). */}
      {!layoutConfig.embedToolbarInPage && showSelectionInfo && (
        <div className={styles.selectionInfo}>
          <Button
            appearance="subtle"
            icon={<Dismiss20Regular />}
            onClick={clearSelection}
          />
          <span>{selectedCount} selected</span>
        </div>
      )}
      {customActions}
      {!layoutConfig.embedToolbarInPage && secondaryButtons.length > 0 && (
        isMobile ? (
          <Menu>
            <MenuTrigger disableButtonEnhancement>
              <ToolbarButton
                icon={<MoreHorizontal24Regular />}
                aria-label="More actions"
              />
            </MenuTrigger>
            <MenuPopover>
              <MenuList>
                {secondaryButtons.flatMap((item, index) => {
                  if (item.customComponent) {
                    const C = item.customComponent;
                    return [<MenuItem key={`sec-${index}`} persistOnClick><C /></MenuItem>];
                  }
                  if (item.menuItems && item.menuItems.length > 0) {
                    return item.menuItems.map((mi: any, mIdx: number) => (
                      <MenuItem
                        key={`sec-${index}-${mIdx}`}
                        icon={mi.icon}
                        onClick={mi.onClick}
                      >
                        {item.label ? `${item.label}: ${mi.label}` : mi.label}
                      </MenuItem>
                    ));
                  }
                  return [
                    <MenuItem
                      key={`sec-${index}`}
                      icon={item.icon}
                      onClick={item.onClick}
                    >
                      {item.label}
                    </MenuItem>,
                  ];
                })}
              </MenuList>
            </MenuPopover>
          </Menu>
        ) : (
          <div className={styles.centerSection}>
            {secondaryButtons.map((item, index) => renderButton(item, index))}
          </div>
        )
      )}
      {!layoutConfig.embedToolbarInPage && primaryButtons.length > 0 && (
        <div className={styles.centerSection}>
          {primaryButtons.map((item, index) => renderButton(item, index))}
        </div>
      )}
    </>
  );

  const headerRow = (
    <div className={`${styles.header}${(inline || stackTitle) ? ' ' + styles.headerInline : ''}`}>
      <div className={styles.leftSection}>
        {/* Conditionally render the sidebar toggle (formerly a generic
            Navigation hamburger; PanelLeft reads more semantically as the
            "show/hide left rail" control). */}
        {layoutConfig.showHamburger !== false && (
          <ToolbarButton
            icon={<PanelLeft24Regular />}
            onClick={toggleSidebar}
            aria-label={sidebarOpen ? 'Hide sidebar' : 'Show sidebar'}
            // invisibleHamburger keeps the slot occupied for chrome
            // alignment but disables visibility + interactivity.
            style={layoutConfig.invisibleHamburger ? { visibility: 'hidden', pointerEvents: 'none' } : undefined}
            tabIndex={layoutConfig.invisibleHamburger ? -1 : undefined}
          />
        )}
        {/* Conditionally render details pane toggle based on layout config */}
        {layoutConfig.showDetailsPane && (
          <ToolbarButton
            icon={<PanelRight24Regular />}
            onClick={toggleDetailsPane}
            appearance={detailsPaneOpen ? 'primary' : 'subtle'}
            aria-label={detailsPaneOpen ? 'Close details pane' : 'Show details pane'}
          />
        )}
        {/* Page-supplied chrome controls (e.g. filter-panel toggle on the
            customers list). Placed alongside the other toggle buttons. */}
        {leftActions}
        {/* Title takes the top line. */}
        {titleSlot}
      </div>

      {/* When stacking, the action cluster drops to the second row below. */}
      {!stackTitle && (
        <div className={styles.rightSection}>{actionsContent}</div>
      )}
    </div>
  );

  if (stackTitle) {
    return (
      <div className={`${styles.stackedWrapper}${inline ? '' : ' ' + styles.stackedSurface}`}>
        {headerRow}
        <div className={styles.secondRow}>{actionsContent}</div>
      </div>
    );
  }

  return headerRow;
};

export default Header;